import { useEffect, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';

import { ToastContext } from '../../../contexts/toast.context';

import AddImage from '../../reusable/images/add-image/add-image.component';
import Button from '../../reusable/button/button.component';
import Spinner from '../../reusable/spinner/spinner.component';
import DynamicProductFields from '../../reusable/inputs/dynamic-product-fields/dynamic-product-fields.component';

import Client from '../../../tools/client';
import DataHelper from '../../../tools/data.helper';

import {
  ContentContainer,
  Label,
  MainContainer,
  Subtitle,
  Text,
  Title
} from '../../../styles/component.styles';

const client = new Client();
const dataHelper = new DataHelper();

const AddProduct = ({ parentCategory = null }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [productTypeSelectData, setProductTypeSelectData] = useState([]);
  const [productType, setProductType] = useState(null);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');
  const [dynamicFields, setDynamicFields] = useState({});
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState('');
  const [cropData, setCropData] = useState(null);

  const { errorToast, successToast } = useContext(ToastContext);

  useEffect(() => {
    if (parentCategory !== null) {
      setCategory(parentCategory.id);
      setLoading(false);
    } else {
      getCategories();
    }
    getProductTypes();
  }, []);

  const getCategories = async () => {
    setLoading(true);
    const res = await client.getAllCategories();
    setCategories(res.rows);
    setLoading(false);
  };

  const getProductTypes = async () => {
    setLoading(true);
    const res = await client.getProductTypes();
    setProductTypeSelectData(res);
    setLoading(false);
  };

  const changeProductType = selected => {
    setProductType(selected);
    setDynamicFields({});
  };

  const createProduct = async () => {
    if (!category || !productType?.id || !name) {
      errorToast('Missing required product information.');
      return;
    }

    const payload = {
      name,
      description,
      categoryId: category,
      productTypeId: productType.id,
      details: dynamicFields
    };

    const formData = new FormData();
    for (const key in payload) {
      if (key === 'details') {
        formData.append('details', JSON.stringify(payload.details));
      } else {
        formData.append(key, payload[key]);
      }
    }

    if (image) {
      formData.append('files', image);
      if (cropData) formData.append('crop', JSON.stringify(cropData));
    }

    setLoading(true);
    const res = await client.createProduct(formData);

    if (res.status === 400) {
      setLoading(false);
      errorToast(res.message);
      return;
    }

    successToast('Product Created');
    navigate(`/products/${res.id}`);
  };

  return (
    <MainContainer minHeight={'50vh'}>
      {loading ? (
        <Spinner />
      ) : categories.length === 0 && parentCategory === null ? (
        <ContentContainer>
          <Title>No Categories</Title>
          <Subtitle>Product creation requires a category. Please create one before adding products.</Subtitle>
          <Button onClick={() => navigate('/categories')}>Categories</Button>
        </ContentContainer>
      ) : (
        <>
          <ContentContainer>
            {parentCategory === null && (
              <>
                <Subtitle>Select A Category:</Subtitle>
                <Label>Category:</Label>
                <Select
                  value={categories.find(c => c.id === category) || null}
                  onChange={selected => setCategory(selected?.id)}
                  options={categories}
                  getOptionLabel={c => c.name}
                  getOptionValue={c => c.id}
                  isClearable
                />
              </>
            )}

            <Subtitle>Select A Product Type:</Subtitle>
            <Label>Product Type:</Label>
            <Select
              value={productTypeSelectData.find(pt => pt.id === productType?.id) || null}
              onChange={changeProductType}
              options={productTypeSelectData}
              getOptionLabel={e => e.name}
              getOptionValue={e => e.id}
              placeholder="Select product type..."
              isClearable
            />
          </ContentContainer>

          {category && productType?.ProductTypeFields?.length > 0 && (
            <>
              <AddImage image={image} setImage={setImage} setCropData={setCropData} />

              <ContentContainer>
                <Label>Product Name:</Label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Enter product name"
                  style={{ width: '100%', marginBottom: '1rem' }}
                />

                <Label>Description:</Label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Enter product description"
                  style={{ width: '100%', minHeight: '80px' }}
                />
              </ContentContainer>

              <DynamicProductFields
                fields={productType.ProductTypeFields}
                values={dynamicFields}
                setValues={setDynamicFields}
              />
              <Button onClick={createProduct}>Create Product</Button>
            </>
          )}
        </>
      )}
    </MainContainer>
  );
};

export default AddProduct;